var __addDisposableResource = (this && this.__addDisposableResource) || function (env, value, async) {
    if (value !== null && value !== void 0) {
        if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
        var dispose, inner;
        if (async) {
            if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
            dispose = value[Symbol.asyncDispose];
        }
        if (dispose === void 0) {
            if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
            dispose = value[Symbol.dispose];
            if (async) inner = dispose;
        }
        if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
        if (inner) dispose = function() { try { inner.call(this); } catch (e) { return Promise.reject(e); } };
        env.stack.push({ value: value, dispose: dispose, async: async });
    }
    else if (async) {
        env.stack.push({ async: true });
    }
    return value;
};
var __disposeResources = (this && this.__disposeResources) || (function (SuppressedError) {
    return function (env) {
        function fail(e) {
            env.error = env.hasError ? new SuppressedError(e, env.error, "An error was suppressed during disposal.") : e;
            env.hasError = true;
        }
        var r, s = 0;
        function next() {
            while (r = env.stack.pop()) {
                try {
                    if (!r.async && s === 1) return s = 0, env.stack.push(r), Promise.resolve().then(next);
                    if (r.dispose) {
                        var result = r.dispose.call(r.value);
                        if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) { fail(e); return next(); });
                    }
                    else s |= 1;
                }
                catch (e) {
                    fail(e);
                }
            }
            if (s === 1) return env.hasError ? Promise.reject(env.error) : Promise.resolve();
            if (env.hasError) throw env.error;
        }
        return next();
    };
})(typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
});
import { setUVMessage, UV } from 'kerium';
import { decodeUTF8 } from 'utilium';
import * as constants from '../constants.js';
import { contextOf } from '../internal/contexts.js';
import { wrap } from '../internal/error.js';
import { hasAccess, isDirectory, isSymbolicLink } from '../internal/inode.js';
import { basename, dirname, join, parse, resolve as resolvePath } from '../path.js';
import { normalizeMode, normalizePath } from '../utils.js';
import { cacheOf, lockPathSync } from './vcache.js';
import { checkAccess } from './config.js';
import { Dirent, ifToDt } from './dir.js';
import { Handle } from './file.js';
import * as flags from './flags.js';
import { resolveMount } from './shared.js';
import { emitChange } from './watchers.js';
/**
 * Resolves the mount and real path for a path.
 * Additionally, any stats fetched will be returned for de-duplication
 * @category VFS
 * @internal @hidden
 */
export function resolve($, path, preserveSymlinks, extra) {
    path = resolvePath.call($, path);
    /* Try to resolve it directly. If this works,
    that means we don't need to perform any resolution for parent directories. */
    try {
        const resolved = resolveMount(path, $);
        // Stat it to make sure it exists. The vnode cache takes precedence since it may have unsynced changes
        const stats = cacheOf(resolved.fs).get(resolved.path)?.inode ?? resolved.fs.statSync(resolved.path);
        if (!isSymbolicLink(stats) || preserveSymlinks) {
            return { ...resolved, fullPath: path, stats };
        }
        const target = resolvePath.call($, dirname(path), readlink.call($, path));
        return resolve($, target, preserveSymlinks, extra);
    }
    catch (e) {
        setUVMessage(Object.assign(e, { syscall: 'stat', path, ...extra }));
        if (preserveSymlinks)
            throw e;
    }
    const { base, dir } = parse(path);
    const realDir = dir == '/' ? '/' : resolve($, dir, false, extra).fullPath;
    const maybePath = join(realDir, base);
    const resolved = resolveMount(maybePath, $);
    let stats;
    try {
        stats = cacheOf(resolved.fs).get(resolved.path)?.inode ?? resolved.fs.statSync(resolved.path);
    }
    catch (e) {
        if (e.code === 'ENOENT')
            return { ...resolved, fullPath: maybePath };
        throw setUVMessage(Object.assign(e, { syscall: 'stat', path: maybePath, ...extra }));
    }
    if (!isSymbolicLink(stats)) {
        return { ...resolved, fullPath: maybePath, stats };
    }
    const target = resolvePath.call($, realDir, readlink.call($, maybePath));
    return resolve($, target, false, extra);
}
/**
 * @category VFS
 * @internal
 */
export function open(path, opt) {
    path = normalizePath(path);
    const mode = normalizeMode(opt.mode, 0o644), flag = flags.parse(opt.flag);
    path = opt.preserveSymlinks ? path : resolve(this, path).fullPath;
    const { fs, path: resolved } = resolveMount(path, this);
    let stats;
    try {
        stats = fs.statSync(resolved);
    }
    catch {
        // nothing
    }
    if (!stats) {
        const env_1 = { stack: [], error: void 0, hasError: false };
        try {
            if (!(flag & constants.O_CREAT)) {
                throw UV('ENOENT', 'open', path);
            }
            // Create the file
            const parentPath = dirname(resolved);
            const parentStats = fs.statSync(parentPath);
            if (checkAccess && !hasAccess(this, parentStats, constants.W_OK)) {
                throw UV('EACCES', 'open', path);
            }
            if (!isDirectory(parentStats)) {
                throw UV('ENOTDIR', 'open', path);
            }
            if (!opt.allowDirectory && mode & constants.S_IFDIR)
                throw UV('EISDIR', 'open', path);
            // Serialize entry creation with other operations on the parent directory
            const _ = __addDisposableResource(env_1, lockPathSync(fs, parentPath, 'rw', parentStats), false);
            const { euid: uid, egid: gid } = contextOf(this).credentials;
            const inode = fs.createFileSync(resolved, {
                mode,
                uid: parentStats.mode & constants.S_ISUID ? parentStats.uid : uid,
                gid: parentStats.mode & constants.S_ISGID ? parentStats.gid : gid,
            });
            // A new entry in the parent directory, which is a 'rename' event
            emitChange(this, 'rename', path);
            return new Handle(this, path, resolved, flag, cacheOf(fs).ref(resolved, inode));
        }
        catch (e_1) {
            env_1.error = e_1;
            env_1.hasError = true;
        }
        finally {
            __disposeResources(env_1);
        }
    }
    if (checkAccess && (!hasAccess(this, stats, mode) || !hasAccess(this, stats, flags.toMode(flag)))) {
        throw UV('EACCES', 'open', path);
    }
    if (flag & constants.O_EXCL)
        throw UV('EEXIST', 'open', path);
    if (!opt.allowDirectory && stats.mode & constants.S_IFDIR)
        throw UV('EISDIR', 'open', path);
    const file = new Handle(this, path, resolved, flag, cacheOf(fs).ref(resolved, stats));
    if (flag & constants.O_TRUNC)
        file.truncateSync(0);
    return file;
}
export function readlink(path) {
    path = normalizePath(path);
    const { fs, stats, path: resolved } = resolve(this, path, true, { syscall: 'readlink' });
    if (!stats)
        throw UV('ENOENT', 'readlink', path);
    if (checkAccess && !hasAccess(this, stats, constants.R_OK))
        throw UV('EACCES', 'readlink', path);
    if (!isSymbolicLink(stats))
        throw UV('EINVAL', 'readlink', path);
    const size = stats.size;
    const data = new Uint8Array(size);
    fs.readSync(resolved, data, 0, size);
    return decodeUTF8(data);
}
export function mkdir(path, options = {}) {
    const original = normalizePath(path);
    const $ex = { syscall: 'mkdir', path: original };
    const { fullPath: realParent } = resolve(this, dirname(original), false, $ex);
    const { fs, path: target } = resolveMount(join(realParent, basename(original)), this, $ex);
    const followed = realParent != dirname(original);
    const { euid: uid, egid: gid } = contextOf(this).credentials;
    const { mode = 0o777, recursive } = options;
    let firstCreated;
    const __create = (path, resolved) => {
        const env_2 = { stack: [], error: void 0, hasError: false };
        try {
            const parentPath = dirname(resolved);
            const parent = recursive && parentPath != '/' ? __create(dirname(path), parentPath) : fs.statSync(parentPath);
            if (recursive) {
                let existing;
                try {
                    existing = cacheOf(fs).get(resolved)?.inode ?? fs.statSync(resolved);
                }
                catch {
                    // It doesn't exist yet
                }
                if (existing) {
                    const stats = isSymbolicLink(existing) ? resolve(this, path, false, $ex).stats : existing;
                    if (!stats)
                        throw UV('ENOENT', $ex);
                    if (!isDirectory(stats))
                        throw UV(resolved == target ? 'EEXIST' : 'ENOTDIR', $ex);
                    return existing;
                }
                if (followed && fs.existsSync(join(parentPath, basename(path))))
                    throw UV('ENOENT', $ex);
            }
            if (checkAccess && !hasAccess(this, parent, constants.W_OK))
                throw UV('EACCES', 'mkdir', path);
            const _ = __addDisposableResource(env_2, lockPathSync(fs, parentPath, 'rw', parent), false);
            const inode = wrap(fs, 'mkdirSync', { path, syscall: 'mkdir' })(resolved, {
                mode,
                uid: parent.mode & constants.S_ISUID ? parent.uid : uid,
                gid: parent.mode & constants.S_ISGID ? parent.gid : gid,
            });
            if (recursive)
                firstCreated ??= path;
            emitChange(this, 'rename', path);
            return inode;
        }
        catch (e_2) {
            env_2.error = e_2;
            env_2.hasError = true;
        }
        finally {
            __disposeResources(env_2);
        }
    };
    __create(original, target);
    return firstCreated;
}
export function readdir(path, options = {}) {
    path = normalizePath(path);
    const { fs, path: resolved } = resolve(this, path);
    // Node reports `readdir` failures as `scandir`
    const stats = cacheOf(fs).get(resolved)?.inode ?? wrap(fs, 'statSync', { path, syscall: 'scandir' })(resolved);
    if (checkAccess && !hasAccess(this, stats, constants.R_OK))
        throw UV('EACCES', 'scandir', path);
    if (!isDirectory(stats))
        throw UV('ENOTDIR', 'scandir', path);
    let entries;
    {
        const env_3 = { stack: [], error: void 0, hasError: false };
        try {
            const _ = __addDisposableResource(env_3, lockPathSync(fs, resolved, 'ro', stats), false);
            entries = fs.readdirSync(resolved);
        }
        catch (e_3) {
            env_3.error = e_3;
            env_3.hasError = true;
        }
        finally {
            __disposeResources(env_3);
        }
    }
    // Iterate over entries and handle recursive case if needed
    const values = [];
    const addEntry = (entry) => {
        let entryStat;
        try {
            entryStat = fs.statSync(join(resolved, entry));
        }
        catch (e) {
            if (e.code == 'ENOENT')
                return;
            throw e;
        }
        const ent = new Dirent();
        ent.ino = entryStat.ino;
        ent.type = ifToDt(entryStat.mode);
        ent.path = entry;
        ent.name = basename(entry);
        values.push(ent);
        if (!isDirectory(entryStat) || !options?.recursive)
            return;
        const children = fs.readdirSync(join(resolved, entry));
        for (const child of children)
            addEntry(join(entry, child));
    };
    for (const entry of entries)
        addEntry(entry);
    return values;
}
export function rename(oldPath, newPath) {
    const env_4 = { stack: [], error: void 0, hasError: false };
    try {
        oldPath = normalizePath(oldPath);
        newPath = normalizePath(newPath);
        const $ex = { syscall: 'rename', path: oldPath, dest: newPath };
        const src = resolve(this, oldPath, true, $ex);
        const dst = resolveMount(newPath, this, $ex);
        if (src.fs.uuid !== dst.fs.uuid)
            throw UV('EXDEV', $ex);
        // A directory can not be moved inside itself
        if (dst.path.startsWith(src.path + '/'))
            throw UV('EINVAL', $ex);
        if (!src.stats)
            throw UV('ENOENT', $ex);
        const fs = src.fs;
        const oldParent = fs.statSync(dirname(src.path));
        const newParent = fs.statSync(dirname(dst.path));
        let newStats;
        try {
            newStats = fs.statSync(dst.path);
        }
        catch (e) {
            if (e.code != 'ENOENT')
                throw e;
        }
        if (checkAccess && (!hasAccess(this, oldParent, constants.R_OK) || !hasAccess(this, newParent, constants.W_OK)))
            throw UV('EACCES', $ex);
        if (newStats && !isDirectory(src.stats) && isDirectory(newStats))
            throw UV('EISDIR', $ex);
        if (newStats && isDirectory(src.stats) && !isDirectory(newStats))
            throw UV('ENOTDIR', $ex);
        const srcDir = dirname(src.path);
        const dstDir = dirname(dst.path);
        // Lock both parent directories, ordered by inode number to avoid ABBA deadlocks (like Linux's `lock_two_nondirectories`)
        const parents = [
            [srcDir, oldParent],
            [dstDir, newParent],
        ];
        if (oldParent.ino > newParent.ino)
            parents.reverse();
        const _first = __addDisposableResource(env_4, lockPathSync(fs, parents[0][0], 'rw', parents[0][1]), false);
        const _second = __addDisposableResource(env_4, oldParent.ino == newParent.ino ? null : lockPathSync(fs, parents[1][0], 'rw', parents[1][1]), false);
        src.fs.renameSync(src.path, dst.path);
        cacheOf(fs).rename(src.path, dst.path);
        // Both names change which entries exist, so both are 'rename' events
        emitChange(this, 'rename', oldPath);
        emitChange(this, 'rename', newPath);
    }
    catch (e_4) {
        env_4.error = e_4;
        env_4.hasError = true;
    }
    finally {
        __disposeResources(env_4);
    }
}
export function link(target, link) {
    const env_5 = { stack: [], error: void 0, hasError: false };
    try {
        target = normalizePath(target);
        link = normalizePath(link);
        const $ex = { syscall: 'link', path: target, dest: link };
        const { fs, path: resolved } = resolveMount(target, this, $ex);
        const dst = resolveMount(link, this, $ex);
        if (fs.uuid !== dst.fs.uuid)
            throw UV('EXDEV', $ex);
        const stats = fs.statSync(resolved);
        if (checkAccess) {
            if (!hasAccess(this, stats, constants.R_OK))
                throw UV('EACCES', $ex);
            const dirStats = fs.statSync(dirname(resolved));
            if (!hasAccess(this, dirStats, constants.R_OK))
                throw UV('EACCES', $ex);
            const destStats = fs.statSync(dirname(dst.path));
            if (!hasAccess(this, destStats, constants.W_OK))
                throw UV('EACCES', $ex);
        }
        const _ = __addDisposableResource(env_5, lockPathSync(fs, dirname(dst.path), 'rw'), false);
        fs.linkSync(resolved, dst.path);
        cacheOf(fs).link(resolved, dst.path);
    }
    catch (e_5) {
        env_5.error = e_5;
        env_5.hasError = true;
    }
    finally {
        __disposeResources(env_5);
    }
}
export function stat(path, lstat) {
    path = normalizePath.call(this, path);
    const extra = { syscall: lstat ? 'lstat' : 'stat', path };
    let stats;
    if (!lstat)
        stats = resolve(this, path, false, extra).stats;
    else {
        const { base, dir } = parse(path);
        const { fs, path: parent } = resolve(this, dir, false, extra);
        try {
            const target = base ? join(parent, base) : parent;
            stats = cacheOf(fs).get(target)?.inode ?? fs.statSync(target);
        }
        catch (e) {
            setUVMessage(Object.assign(e, extra));
            throw e;
        }
    }
    if (!stats)
        throw UV('ENOENT', extra);
    if (checkAccess && !hasAccess(this, stats, constants.R_OK))
        throw UV('EACCES', extra);
    return stats;
}
